import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search, X, FileText, ArrowRight, LifeBuoy, ShieldCheck } from "lucide-react";
import Seo, { SITE_URL } from "@/components/Seo";
import { FAQ_CATEGORIES, activeFaqs } from "@/data/faqData";

const track = (event, props) => { try { window.posthog?.capture(event, props); } catch (e) { /* noop */ } };

export default function FAQ() {
  const all = useMemo(() => activeFaqs(), []);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState("");

  // Deep-link: /faq#faq-id opens that question.
  useEffect(() => {
    const h = (window.location.hash || "").replace(/^#/, "");
    if (h) { setOpen(h); const el = document.getElementById(h); if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); }
  }, []);

  const q = query.trim().toLowerCase();
  const results = useMemo(() => {
    let list = all;
    if (cat !== "All") list = list.filter((f) => f.category === cat);
    if (q) list = list.filter((f) =>
      (f.q + " " + f.a + " " + f.category + " " + (f.keywords || []).join(" ")).toLowerCase().includes(q));
    return list;
  }, [all, cat, q]);

  const faqJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": all.map((f) => ({
      "@type": "Question", "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  }), [all]);

  const onSearch = (v) => { setQuery(v); if (v.trim().length > 2) track("faq_search", { q: v.trim() }); };
  const onCat = (c) => { setCat(c); track("faq_category_selected", { category: c }); };

  return (
    <main className="pt-32 pb-24" data-testid="faq-page">
      <Seo
        title="Research FAQ & Support"
        description="Find answers about Molecularix Biolabs research materials, product specifications, documentation, ordering, shipping, and Research Use Only requirements."
        path="/faq"
        jsonLd={faqJsonLd}
      />
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Hero */}
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Molecularix Research Support</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-[1.02] mb-5">Research Support Center</motion.h1>
        <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed max-w-2xl mb-8">
          Everything you need to understand our research materials, documentation, ordering process, and Research Use Only requirements.
        </p>

        {/* Search */}
        <div className="relative max-w-xl mb-8">
          <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]" aria-hidden="true" />
          <input
            value={query} onChange={(e) => onSearch(e.target.value)}
            type="search" aria-label="Search Research Support"
            placeholder="Search Research Support…" data-testid="faq-search"
            className="w-full bg-transparent border border-zinc-800 focus:border-[#D4AF37] outline-none pl-11 pr-11 py-3.5 font-mono text-sm placeholder:text-[#52525B] transition-colors" />
          {query && (
            <button onClick={() => setQuery("")} aria-label="Clear search" data-testid="faq-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#D4AF37] transition-colors">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-8 -mx-1 px-1 scrollbar-none" role="tablist" aria-label="FAQ categories" data-testid="faq-categories">
          {["All", ...FAQ_CATEGORIES].map((c) => (
            <button key={c} onClick={() => onCat(c)} role="tab" aria-selected={cat === c}
              data-testid={`faq-cat-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors whitespace-nowrap ${
                cat === c ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-zinc-800 text-[#A1A1AA] hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"}`}>
              {c}
            </button>
          ))}
        </div>

        {/* Result count when searching */}
        {q && (
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-4" data-testid="faq-result-count">
            {results.length} result{results.length === 1 ? "" : "s"}
          </p>
        )}

        {/* Results */}
        {results.length === 0 ? (
          <div className="border border-zinc-800 bg-[#0C0C0C] p-10 text-center" data-testid="faq-empty">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">No matching results</p>
            <p className="text-[#A1A1AA] text-sm mb-6">We couldn&apos;t find a research-support article matching your search.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={() => { setQuery(""); setCat("All"); }} data-testid="faq-clear-btn"
                className="border border-zinc-700 text-[#A1A1AA] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">Clear search</button>
              <Link to="/contact" data-testid="faq-empty-contact"
                className="bg-[#D4AF37] text-black px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors">Contact support</Link>
            </div>
          </div>
        ) : (
          <Accordion type="single" collapsible value={open} onValueChange={(v) => { setOpen(v); if (v) track("faq_question_opened", { id: v }); }} className="w-full">
            {results.map((f) => (
              <AccordionItem key={f.id} id={f.id} value={f.id} className="border-b border-zinc-800" data-testid={`faq-item-${f.id}`}>
                <AccordionTrigger className="font-serif text-lg md:text-xl font-light text-left hover:text-[#D4AF37] hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#A1A1AA] text-[15px] leading-relaxed pb-6">
                  <p>{f.a}</p>
                  {f.links && f.links.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-4">
                      {f.links.map((l) => (
                        <Link key={l.to} to={l.to} data-testid={`faq-link-${f.id}`}
                          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] hover:underline">
                          {l.label} <ArrowRight size={12} />
                        </Link>
                      ))}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}

        {/* Documentation callout */}
        <div className="mt-16 grid sm:grid-cols-2 gap-4">
          <div className="border border-zinc-800 bg-[#0C0C0C] p-8" data-testid="faq-docs-callout">
            <FileText size={20} className="text-[#D4AF37] mb-4" />
            <h2 className="font-serif text-2xl font-light mb-2">Need documentation?</h2>
            <p className="text-[#71717A] text-sm leading-relaxed mb-5">Looking for product specifications or batch documentation?</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/documentation" onClick={() => track("documentation_cta_clicked")} data-testid="faq-view-docs"
                className="bg-[#D4AF37] text-black px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors">View documentation</Link>
              <Link to="/verify" data-testid="faq-verify-batch"
                className="border border-zinc-700 text-[#A1A1AA] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">Verify a batch</Link>
            </div>
          </div>
          <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] p-8" data-testid="faq-ruo-callout">
            <ShieldCheck size={20} className="text-[#D4AF37] mb-4" />
            <h2 className="font-serif text-2xl font-light mb-2">Research Use Only</h2>
            <p className="text-[#71717A] text-sm leading-relaxed mb-5">Molecularix Biolabs research materials are intended solely for legitimate laboratory and in-vitro research, and are not for human or veterinary use.</p>
            <Link to="/policy/research-use" data-testid="faq-ruo-link"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] hover:underline">Read full policy <ArrowRight size={12} /></Link>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-8 border border-zinc-800 bg-[#0C0C0C] p-10 text-center" data-testid="faq-contact-cta">
          <LifeBuoy size={22} className="text-[#D4AF37] mx-auto mb-4" />
          <h3 className="font-serif text-3xl font-light mb-3">Still have questions?</h3>
          <p className="text-[#A1A1AA] text-sm max-w-lg mx-auto mb-7">Can&apos;t find what you&apos;re looking for? Our support team can help with product information, documentation, orders, and general research-material questions.</p>
          <Link to="/contact" onClick={() => track("contact_support_clicked")} data-testid="faq-contact-btn"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors">
            Contact Molecularix Support <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </main>
  );
}
